import { UseFormReturn, FieldValues, FieldPath } from 'react-hook-form';

import { useIntl } from 'react-intl';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';

type SelectFormFieldProps<TData extends FieldValues> = {
    form: UseFormReturn<TData>;
    name: FieldPath<TData>;
    label: string;
    options?: string[];
    optionTranslationMap: {
        [key: string]: { id: string; defaultMessage: string };
    };
    isBoolean?: boolean;
    required?: boolean;
    isDisabled?: boolean;
    onChange?: (v: string) => void;
};

export default function SelectFormField<TData extends FieldValues>({
    name,
    label,
    options,
    form,
    required = true,
    isBoolean,
    optionTranslationMap,
    onChange,
    isDisabled = false,
}: SelectFormFieldProps<TData>) {
    const { formatMessage } = useIntl();
    if (!options) return null;
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => {
                return (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required={required}>
                            {label}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={(value) => {
                                        const newValue = isBoolean ? JSON.parse(value) : value;
                                        if (onChange) onChange(newValue);
                                        field.onChange(newValue);
                                    }}
                                    value={field.value?.toString()}
                                    disabled={isDisabled}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${label}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {options?.map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {formatMessage(optionTranslationMap[option])}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                );
            }}
        />
    );
}
